import { Link } from "react-router-dom";
import { CatalogProduct } from "@/lib/catalog";
import { useCartStore } from "@/stores/cartStore";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ProductCardReviews } from "./ProductCardReviews";
import { PriceDisplay } from "./PriceDisplay";
import { resizeImageUrl, buildSrcSet } from "@/lib/imageUrl";

interface VariantCardProps {
  product: CatalogProduct;
  variantIdx: number;
}

export const VariantCard = ({ product, variantIdx }: VariantCardProps) => {
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  const { node } = product;
  const variant = node.variants.edges[variantIdx]?.node;
  const variantImage = variant?.image;
  const fallbackImage = node.images.edges[0]?.node;
  const image = variantImage || fallbackImage;

  if (!variant) return null;

  const isDefaultVariant = variant.title === "Default Title";
  const displayTitle = isDefaultVariant ? node.title : `${node.title} — ${variant.title}`;

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
    toast.success("Added to cart", { description: displayTitle });
  };

  return (
    <Link
      to={`/product/${node.handle}?variant=${encodeURIComponent(variant.id)}`}
      className="group block opacity-0 animate-fade-in"
    >
      <div className="aspect-square overflow-hidden rounded-sm bg-card mb-4">
        {image ? (
          <img
            src={resizeImageUrl(image.url, 640)}
            srcSet={buildSrcSet(image.url, [320, 480, 640])}
            sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 600px"
            alt={image.altText || `${displayTitle} — Param Kesar®`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            decoding="async"
            width={600}
            height={600}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            No image
          </div>
        )}
      </div>
      <div className="space-y-2">
        <h3 className="font-display text-lg font-semibold leading-tight group-hover:text-primary transition-colors line-clamp-2">
          {displayTitle}
        </h3>
        {!isDefaultVariant && (
          <p className="text-muted-foreground text-xs">
            {variant.selectedOptions.map(o => `${o.name}: ${o.value}`).join(' · ')}
          </p>
        )}
        <ProductCardReviews variantId={variant.id} productHandle={node.handle} />
        <p className="text-muted-foreground text-sm line-clamp-2">
          {(() => {
            const variantDesc = variant.descriptionHtml?.value;
            if (variantDesc) {
              // Strip HTML tags for card preview
              const text = variantDesc.replace(/<[^>]*>/g, '');
              return text.slice(0, 100) + (text.length > 100 ? '...' : '');
            }
            return (node.description?.slice(0, 100) || '') + '...';
          })()}
        </p>
        <div className="flex items-center justify-between gap-2 pt-1">
          <PriceDisplay
            price={variant.price}
            compareAtPrice={variant.compareAtPrice ?? null}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={handleAddToCart}
            disabled={isLoading || !variant.availableForSale}
            className="gap-1.5"
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ShoppingCart className="h-3.5 w-3.5" />
            )}
            {variant.availableForSale ? "Add" : "Sold Out"}
          </Button>
        </div>
      </div>
    </Link>
  );
};
